import React, { useState, useEffect } from 'react';
import './dash.css';
import dashOne from '../Images/dashone.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { db } from '../firebase';
import { collection, query, getDocs, getCountFromServer, where, Timestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { normalizeAdminType, resolveAdminRecord } from '../utils/adminAccess';
import {
  getMonthKey,
  isSuccessfulBillPayment,
  isSuccessfulSalaryPayment,
  normalizeAmount,
  toDateValue,
  filterSchoolFeeIncomeRecords,
  resolveBillPaymentPeriodKey,
  resolveRequisitionPaymentPeriodKey,
  resolveSalaryPaymentPeriodKey
} from '../utils/financeWorkflow';

const MONTHLY_TARGET = 1000000;
const TARGET_SAVINGS_RATE = 0.2;
const DASH_PRIORITY_COLOR = '#2563eb';
const DASH_FORECAST_COLORS = ['#0ea5e9', '#14b8a6', '#6366f1', '#f97316'];

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DASHBOARD_TIME_ZONE = 'Africa/Nairobi';

const fetchDashboardMonthDocs = async (collectionName, { dateFields = [], monthKeyFields = [] } = {}, startDate, endDate, monthKey) => {
  const source = collection(db, collectionName);
  const requests = [
    ...dateFields.map((field) => getDocs(query(
      source,
      where(field, '>=', Timestamp.fromDate(startDate)),
      where(field, '<', Timestamp.fromDate(endDate))
    ))),
    ...monthKeyFields.map((field) => getDocs(query(source, where(field, '==', monthKey))))
  ];
  const snapshots = await Promise.all(requests);
  const docsById = new Map();

  snapshots.forEach((snapshot) => snapshot.docs.forEach((doc) => {
    docsById.set(doc.id, { id: doc.id, ...doc.data() });
  }));

  return Array.from(docsById.values());
};

const fetchDashboardYearDocs = async (collectionName, dateFields, startDate, endDate) => {
  const source = collection(db, collectionName);
  const snapshots = await Promise.all(dateFields.map((field) => getDocs(query(
    source,
    where(field, '>=', Timestamp.fromDate(startDate)),
    where(field, '<', Timestamp.fromDate(endDate))
  ))));
  const docsById = new Map();

  snapshots.forEach((snapshot) => snapshot.docs.forEach((doc) => {
    docsById.set(doc.id, { id: doc.id, ...doc.data() });
  }));

  return Array.from(docsById.values());
};

const DashboardStatValue = ({ loading, error, label, children, skeletonWidth = '5rem' }) => {
  if (loading) {
    return (
      <span className="dashStatLoading" role="status" aria-label={`Loading ${label}`}>
        <span className="dashStatSkeleton" style={{ width: skeletonWidth }} />
      </span>
    );
  }

  if (error) {
    return <span className="dashStatUnavailable" role="status">Unavailable</span>;
  }

  return children;
};

const INTAKE_JOINED_STATUSES = new Set([
  'student',
  'active',
  'completed',
  'paused',
  'joined',
  'registered',
  'ongoing'
]);

const INTAKE_PENDING_STATUSES = new Set([
  'inactive',
  'pending',
  'applied',
  'applicant',
  'application',
  'application pending',
  'prospect',
  'prospective',
  'shortlisted',
  'waiting',
  'follow up',
  'follow-up',
  'deferred'
]);

const INTAKE_LEFT_AFTER_JOINING_STATUSES = new Set([
  'dropped',
  'expelled'
]);

const normalizeDashboardText = (value) => String(value || '').trim().toLowerCase();

const toDashboardDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getDashboardMonthKey = (dateValue) => `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}`;

const getDashboardMonthLabel = (dateValue) => dateValue.toLocaleDateString('en-KE', {
  timeZone: DASHBOARD_TIME_ZONE,
  month: 'short',
  year: 'numeric'
});

const buildCurrentYearMonths = (year, monthIndex = new Date().getMonth()) => (
  Array.from({ length: monthIndex + 1 }, (_, index) => {
    const monthDate = new Date(year, index, 1);
    return {
      monthKey: getDashboardMonthKey(monthDate),
      monthLabel: getDashboardMonthLabel(monthDate)
    };
  })
);

const buildYearMonths = (year) => (
  Array.from({ length: 12 }, (_, index) => {
    const monthDate = new Date(year, index, 1);
    return {
      monthKey: getDashboardMonthKey(monthDate),
      monthLabel: getDashboardMonthLabel(monthDate)
    };
  })
);

const getIntakeOutcomeBucket = (value) => {
  const status = normalizeDashboardText(value);

  if (!status || INTAKE_PENDING_STATUSES.has(status)) {
    return 'pending';
  }

  if (status === 'did not join') {
    return 'didNotJoin';
  }

  if (INTAKE_LEFT_AFTER_JOINING_STATUSES.has(status)) {
    return 'leftAfterJoining';
  }

  if (INTAKE_JOINED_STATUSES.has(status)) {
    return 'joined';
  }

  return 'pending';
};

const formatSignedPercent = (value, digits = 1) => {
  const safeValue = Number.isFinite(value) ? value : 0;
  const prefix = safeValue > 0 ? '+' : '';
  return `${prefix}${safeValue.toFixed(digits)}%`;
};

const getTrackedSuccessRate = (row = {}) => {
  const tracked = (Number(row.joined) || 0) + (Number(row.didNotJoin) || 0);
  return tracked > 0 ? Math.round(((Number(row.joined) || 0) / tracked) * 100) : 0;
};

const TARGET_PUSH_COURSES = [
  {
    label: 'Data Science',
    aliases: ['data science', 'datascience']
  },
  {
    label: 'Applied Machine Learning',
    aliases: ['applied machine learning', 'machine learning', 'ai machine learning']
  },
  {
    label: 'UI/UX',
    aliases: ['ui/ux', 'ui ux', 'ux/ui', 'user interface', 'user experience']
  },
  {
    label: 'Full Stack Software Development',
    aliases: ['full stack software development', 'full stack development', 'full stack web development', 'software development']
  }
];

const normalizeCourseKey = (value) => normalizeDashboardText(value).replace(/[^a-z0-9]+/g, '');

const resolveDashboardCourseName = (data = {}) => String(
  data.course
  || data.program
  || data.programme
  || data.CourseName
  || data.courseName
  || ''
).trim();

const resolveDashboardCourseMonthlyFee = (course = {}) => {
  const installment = Number(course.feeInstallments || course.monthlyFee || course.monthlyInstallment || 0) || 0;
  if (installment > 0) {
    return installment;
  }

  const totalFee = Number(course.mcoursePrice || course.courseFee || course.fee || 0) || 0;
  const durationMonths = Number(course.courseDuration || course.durationMonths || 0) || 0;

  if (totalFee > 0 && durationMonths > 0) {
    return totalFee / durationMonths;
  }

  return 0;
};

const buildCourseBucket = (bucket, courseKey, courseName, monthlyFee = 0) => {
  if (!bucket.has(courseKey)) {
    bucket.set(courseKey, {
      courseKey,
      courseName,
      monthlyFee,
      applicants: 0,
      joined: 0,
      pending: 0,
      didNotJoin: 0,
      currentMonthApplicants: 0,
      currentMonthJoined: 0,
      currentMonthDidNotJoin: 0,
      currentMonthPending: 0,
      currentMonthProjectedIncome: 0,
      currentYearIncome: 0,
      successRate: 0,
      priorityScore: 0,
      studentsToOneMillion: 0,
      growthLabel: 'Watch'
    });
  }

  return bucket.get(courseKey);
};

const matchDashboardCourseDefinition = (courseName, courseDefinitions = []) => {
  const normalized = normalizeCourseKey(courseName);
  if (!normalized) {
    return null;
  }

  return courseDefinitions.find((course) => {
    const target = course.normalizedName;
    return target === normalized || target.includes(normalized) || normalized.includes(target);
  }) || null;
};

const matchDashboardTargetCourse = (targetCourse, courseDefinitions = []) => {
  const searchTerms = [targetCourse?.label, ...(targetCourse?.aliases || [])].filter(Boolean);
  for (const term of searchTerms) {
    const matchedCourse = matchDashboardCourseDefinition(term, courseDefinitions);
    if (matchedCourse) {
      return matchedCourse;
    }
  }

  return null;
};

function DashComp() {
  // States
  const [loggedInUser, setLoggedInUser] = useState('');
  const [adminRole, setAdminRole] = useState('admin');
  const [showFinancialData, setShowFinancialData] = useState(false);
  const [totalApplicants, setTotalApplicants] = useState(0);
  const [activeStudents, setActiveStudents] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [statStates, setStatStates] = useState({
    totalApplicants: 'loading',
    activeStudents: 'loading',
    income: 'loading',
    expenses: 'loading'
  });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyData, setMonthlyData] = useState({
    Jan: 0, Feb: 0, Mar: 0, Apr: 0, May: 0, Jun: 0,
    Jul: 0, Aug: 0, Sep: 0, Oct: 0, Nov: 0, Dec: 0
  });
  const [analytics, setAnalytics] = useState({
    intakeSnapshot: {
      applicants: 0,
      joined: 0,
      pending: 0,
      didNotJoin: 0,
      leftAfterJoining: 0,
      successRate: 0,
      latestMonthLabel: 'No intake data',
      latestMonthApplicants: 0,
      latestMonthJoined: 0,
      latestMonthSuccessRate: 0,
      monthOverMonth: 0
    },
    intakeTrend: [],
    intake: {
      last7: 0,
      prev7: 0,
      last30: 0,
      prev30: 0
    },
    topSources: [],
    topSourcesCurrentYear: [],
    topSourcesLatest: [],
    courseOpportunity: [],
    pushCourses: [],
    priorityCourses: [],
    growthForecast: {
      currentMonthApplications: 0,
      predictedJoins: 0,
      projectedIncome: 0,
      successRate: 0,
      portfolioMonthlyFee: 0,
      portfolioStudentsToOneMillion: 0,
      portfolioProjectedIncome: 0,
      forecastMonths: [],
      forecastSeries: [],
      forecastCurrentMonthIncome: 0,
      forecastYearIncome: 0
    },
    completionByCourse: [],
    dropReasons: [],
    cohortProgress: [],
    instructorLoad: [],
    debtRisk: {
      totalPrincipal: 0,
      dueSoon: 0,
      totalCount: 0
    }
  });

  // Generate list of years from 2020 to the current year
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2020; year <= currentYear; year++) {
    years.push(year);
  }

  // Function to handle button clicks
  const handleClick = (year) => {
    console.log(`Button clicked for year: ${year}`);
    setSelectedYear(year);
  };

  // Fetch data for the selected year
  useEffect(() => {
    const fetchData = async () => {
      const yearStart = new Date(selectedYear, 0, 1);
      const nextYearStart = new Date(selectedYear + 1, 0, 1);
      const [incomeRecords, admissionFeeRecords] = await Promise.all([
        fetchDashboardYearDocs('Income', ['paymentDate', 'timeCreated'], yearStart, nextYearStart),
        fetchDashboardYearDocs('AdmissionFeePayments', ['paymentDate', 'createdAt', 'admissionFeePaidAt'], yearStart, nextYearStart)
      ]);
      const data = {
        Jan: 0, Feb: 0, Mar: 0, Apr: 0, May: 0, Jun: 0,
        Jul: 0, Aug: 0, Sep: 0, Oct: 0, Nov: 0, Dec: 0
      };
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      filterSchoolFeeIncomeRecords(incomeRecords).forEach((income) => {
        const paymentDate = toDateValue(income.paymentDate || income.timeCreated);
        if (!paymentDate || paymentDate < yearStart || paymentDate >= nextYearStart) return;
        const month = paymentDate.getMonth(); // 0 for January, 11 for December
        data[monthNames[month]] += normalizeAmount(income.incomeAmount || income.amount);
      });

      admissionFeeRecords.forEach((payment) => {
        const paymentDate = toDateValue(payment.paymentDate || payment.createdAt || payment.admissionFeePaidAt);
        if (!paymentDate || paymentDate < yearStart || paymentDate >= nextYearStart) {
          return;
        }

        const month = paymentDate.getMonth();
        data[monthNames[month]] += normalizeAmount(payment.amount || payment.admissionFeeAmount);
      });

      setMonthlyData(data);
    };

    fetchData();
  }, [selectedYear]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const q = query(collection(db, 'users'), where('userStatus', '==', 'inactive'));
        const countSnapshot = await getCountFromServer(q);
        setTotalApplicants(countSnapshot.data().count);
      } catch (error) {
        console.error('Failed to load total applications:', error);
        setStatStates((current) => ({ ...current, totalApplicants: 'error' }));
      } finally {
        setStatStates((current) => current.totalApplicants === 'error'
          ? current
          : { ...current, totalApplicants: 'loaded' });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (adminRole !== 'superAdmin') {
        return;
      }

      try {
        const [usersSnap, coursesSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'courses'))
        ]);
        const sourceCounts = {};
        const sourceCountsLatest = {};
        const courseStats = {};
        const courseDefinitions = coursesSnap.docs
          .map((doc) => {
            const data = doc.data();
            const courseName = String(data.mcourseName || data.courseName || '').trim();
            if (!courseName) {
              return null;
            }

            return {
              id: doc.id,
              name: courseName,
              normalizedName: normalizeCourseKey(courseName),
              monthlyFee: resolveDashboardCourseMonthlyFee(data)
            };
          })
          .filter(Boolean);
        const courseDefinitionMap = new Map(
          courseDefinitions.map((course) => [course.normalizedName, course])
        );
        const cohortStats = {};
        const dropReasonCounts = {};
        const intakeRows = [];
        const sourceCountsCurrentYear = {};

        const now = new Date();
        const sevenAgo = new Date(now);
        sevenAgo.setDate(now.getDate() - 7);
        const fourteenAgo = new Date(now);
        fourteenAgo.setDate(now.getDate() - 14);
        const thirtyAgo = new Date(now);
        thirtyAgo.setDate(now.getDate() - 30);
        const sixtyAgo = new Date(now);
        sixtyAgo.setDate(now.getDate() - 60);
        let last7 = 0;
        let prev7 = 0;
        let last30 = 0;
        let prev30 = 0;

        usersSnap.forEach((doc) => {
          const data = doc.data();
          const status = String(data.userStatus || '').trim();
          const statusLower = status.toLowerCase();
          const appDate = toDashboardDate(data.appDate || data.createdAt || data.admissionDate || data.classStartDate);
          const rawCourseName = resolveDashboardCourseName(data);
          const matchedCourse = matchDashboardCourseDefinition(rawCourseName, courseDefinitions);
          const resolvedCourseName = matchedCourse?.name || rawCourseName || 'Unknown';
          const courseKey = normalizeCourseKey(resolvedCourseName || rawCourseName || doc.id);
          if (appDate) {
            intakeRows.push({
              year: appDate.getFullYear(),
              monthKey: getDashboardMonthKey(appDate),
              outcome: getIntakeOutcomeBucket(statusLower),
              courseKey,
              courseName: resolvedCourseName,
              monthlyFee: matchedCourse?.monthlyFee || 0
            });
            if (appDate >= sevenAgo) last7 += 1;
            else if (appDate >= fourteenAgo) prev7 += 1;

            if (appDate >= thirtyAgo) last30 += 1;
            else if (appDate >= sixtyAgo) prev30 += 1;
          }

          const source = String(data.hear || data.source || 'Unknown').trim() || 'Unknown';
          sourceCounts[source] = (sourceCounts[source] || 0) + 1;
          if (appDate && appDate.getFullYear() === currentYear) {
            sourceCountsCurrentYear[source] = (sourceCountsCurrentYear[source] || 0) + 1;
          }
          if (appDate && appDate >= thirtyAgo) {
            sourceCountsLatest[source] = (sourceCountsLatest[source] || 0) + 1;
          }

          const course = String(data.course || 'Unknown').trim() || 'Unknown';
          if (!courseStats[course]) {
            courseStats[course] = { total: 0, completed: 0 };
          }
          courseStats[course].total += 1;
          if (statusLower === 'completed') {
            courseStats[course].completed += 1;
          }

          const cohort = String(data.cohort || 'Unknown').trim() || 'Unknown';
          if (!cohortStats[cohort]) {
            cohortStats[cohort] = { total: 0, completed: 0, active: 0 };
          }
          cohortStats[cohort].total += 1;
          if (statusLower === 'completed') cohortStats[cohort].completed += 1;
          if (statusLower === 'student') cohortStats[cohort].active += 1;

          if (statusLower === 'did not join') {
            const reason = String(data.notJoiningReason || 'No reason').trim() || 'No reason';
            dropReasonCounts[reason] = (dropReasonCounts[reason] || 0) + 1;
          }
        });

        const currentYearMonths = buildCurrentYearMonths(currentYear, new Date().getMonth());
        const intakeTrendMap = new Map(currentYearMonths.map(({ monthKey, monthLabel }) => ([
          monthKey,
          {
            monthKey,
            monthLabel,
            applicants: 0,
            joined: 0,
            pending: 0,
            didNotJoin: 0,
            leftAfterJoining: 0,
            successRate: 0
          }
        ])));
        const intakeSnapshot = {
          applicants: 0,
          joined: 0,
          pending: 0,
          didNotJoin: 0,
          leftAfterJoining: 0,
          successRate: 0,
          latestMonthLabel: currentYearMonths[currentYearMonths.length - 1]?.monthLabel || String(currentYear),
          latestMonthApplicants: 0,
          latestMonthJoined: 0,
          latestMonthSuccessRate: 0,
          monthOverMonth: 0
        };

        const currentYearIntakeRows = intakeRows.filter((row) => row.year === currentYear);
        const currentMonthKey = getDashboardMonthKey(new Date());
        const currentYearCourseStats = new Map();
        const currentMonthCourseStats = new Map();

        currentYearIntakeRows.forEach((row) => {
          const courseKey = row.courseKey || normalizeCourseKey(row.courseName || 'Unknown');
          const courseMeta = courseDefinitionMap.get(courseKey) || {
            name: row.courseName || 'Unknown',
            monthlyFee: row.monthlyFee || 0
          };
          const courseBucket = buildCourseBucket(
            currentYearCourseStats,
            courseKey,
            courseMeta.name || row.courseName || 'Unknown',
            courseMeta.monthlyFee || row.monthlyFee || 0
          );

          courseBucket.applicants += 1;
          if (row.outcome === 'joined') {
            courseBucket.joined += 1;
            courseBucket.currentYearIncome += courseBucket.monthlyFee;
          } else if (row.outcome === 'didNotJoin') {
            courseBucket.didNotJoin += 1;
          } else if (row.outcome === 'leftAfterJoining') {
            courseBucket.didNotJoin += 1;
          } else {
            courseBucket.pending += 1;
          }

          if (row.monthKey === currentMonthKey) {
            const monthBucket = buildCourseBucket(
              currentMonthCourseStats,
              courseKey,
              courseMeta.name || row.courseName || 'Unknown',
              courseMeta.monthlyFee || row.monthlyFee || 0
            );
            monthBucket.applicants += 1;
            if (row.outcome === 'joined') {
              monthBucket.joined += 1;
            } else if (row.outcome === 'didNotJoin' || row.outcome === 'leftAfterJoining') {
              monthBucket.didNotJoin += 1;
            } else {
              monthBucket.pending += 1;
            }
          }
        });

        const currentYearCourseRows = Array.from(currentYearCourseStats.values())
          .map((row) => {
            const successRate = getTrackedSuccessRate(row);
            const priorityScore = (row.applicants * row.monthlyFee) * (0.45 + (successRate / 100));

            return {
              ...row,
              successRate,
              priorityScore,
              studentsToOneMillion: row.monthlyFee > 0 ? Math.ceil(1000000 / row.monthlyFee) : 0,
              currentYearIncome: row.joined * row.monthlyFee,
              growthLabel: successRate >= 60
                ? 'Strong push'
                : row.applicants >= 20
                  ? 'Steady demand'
                  : 'Develop'
            };
          })
          .filter((row) => row.monthlyFee > 0)
          .sort((first, second) => second.priorityScore - first.priorityScore || second.applicants - first.applicants);

        currentYearIntakeRows.forEach((row) => {
          const trendRow = intakeTrendMap.get(row.monthKey);
          intakeSnapshot.applicants += 1;

          if (row.outcome === 'joined') {
            intakeSnapshot.joined += 1;
          } else if (row.outcome === 'didNotJoin') {
            intakeSnapshot.didNotJoin += 1;
          } else if (row.outcome === 'leftAfterJoining') {
            intakeSnapshot.leftAfterJoining += 1;
          } else {
            intakeSnapshot.pending += 1;
          }

          if (trendRow) {
            trendRow.applicants += 1;
            if (row.outcome === 'joined') {
              trendRow.joined += 1;
            } else if (row.outcome === 'didNotJoin') {
              trendRow.didNotJoin += 1;
            } else if (row.outcome === 'leftAfterJoining') {
              trendRow.leftAfterJoining += 1;
            } else {
              trendRow.pending += 1;
            }
          }
        });

        const intakeTrend = currentYearMonths.map(({ monthKey, monthLabel }) => {
          const row = intakeTrendMap.get(monthKey) || {
            monthKey,
            monthLabel,
            applicants: 0,
            joined: 0,
            pending: 0,
            didNotJoin: 0,
            leftAfterJoining: 0,
            successRate: 0
          };

          return {
            ...row,
            successRate: getTrackedSuccessRate(row)
          };
        });

        const latestTrendMonth = intakeTrend[intakeTrend.length - 1] || intakeSnapshot;
        const previousTrendMonth = intakeTrend[intakeTrend.length - 2] || null;
        intakeSnapshot.successRate = getTrackedSuccessRate(intakeSnapshot);
        intakeSnapshot.latestMonthLabel = latestTrendMonth.monthLabel || intakeSnapshot.latestMonthLabel;
        intakeSnapshot.latestMonthApplicants = latestTrendMonth.applicants || 0;
        intakeSnapshot.latestMonthJoined = latestTrendMonth.joined || 0;
        intakeSnapshot.latestMonthSuccessRate = getTrackedSuccessRate(latestTrendMonth);
        intakeSnapshot.monthOverMonth = previousTrendMonth
          ? getTrackedSuccessRate(latestTrendMonth) - getTrackedSuccessRate(previousTrendMonth)
          : 0;

        const currentYearSuccessRateDecimal = intakeSnapshot.successRate / 100;
        const currentMonthCourseRows = Array.from(currentMonthCourseStats.values())
          .map((row) => ({
            ...row,
            successRate: getTrackedSuccessRate(row),
            projectedIncome: Math.round((Number(row.applicants) || 0) * currentYearSuccessRateDecimal * (Number(row.monthlyFee) || 0))
          }))
          .filter((row) => row.monthlyFee > 0)
          .sort((first, second) => second.projectedIncome - first.projectedIncome || second.applicants - first.applicants);
        const currentMonthApplications = currentMonthCourseRows.reduce((sum, row) => sum + (Number(row.applicants) || 0), 0);
        const predictedJoinsThisMonth = Math.round(currentMonthApplications * currentYearSuccessRateDecimal);
        const predictedIncomeThisMonth = Math.round(currentMonthCourseRows.reduce((sum, row) => sum + row.projectedIncome, 0));

        const courseOpportunity = currentYearCourseRows.slice(0, 4).map((row) => ({
          ...row,
          projectedIncome: row.currentYearIncome
        }));

        const priorityCourses = currentYearCourseRows.slice(0, 5).map((row, index) => ({
          ...row,
          rank: index + 1
        }));

        const pushCourses = TARGET_PUSH_COURSES.map((targetCourse) => {
          const matchedCourse = matchDashboardTargetCourse(targetCourse, courseDefinitions);
          const courseKey = matchedCourse?.normalizedName || normalizeCourseKey(targetCourse.label);
          const yearStats = currentYearCourseStats.get(courseKey) || buildCourseBucket(
            currentYearCourseStats,
            courseKey,
            matchedCourse?.name || targetCourse.label,
            matchedCourse?.monthlyFee || 0
          );
          const monthStats = currentMonthCourseStats.get(courseKey) || buildCourseBucket(
            currentMonthCourseStats,
            courseKey,
            matchedCourse?.name || targetCourse.label,
            matchedCourse?.monthlyFee || 0
          );
          const monthlyFee = matchedCourse?.monthlyFee || yearStats.monthlyFee || 0;
          const successRate = getTrackedSuccessRate(yearStats);
          const predictedMonthJoins = Math.round((Number(monthStats.applicants) || 0) * currentYearSuccessRateDecimal);
          const projectedMonthIncome = Math.round(predictedMonthJoins * monthlyFee);

          return {
            courseKey,
            courseName: matchedCourse?.name || targetCourse.label,
            monthlyFee,
            applicants: yearStats.applicants,
            joined: yearStats.joined,
            didNotJoin: yearStats.didNotJoin,
            successRate,
            currentYearIncome: yearStats.currentYearIncome,
            studentsToOneMillion: monthlyFee > 0 ? Math.ceil(1000000 / monthlyFee) : 0,
            currentMonthApplications: monthStats.applicants,
            predictedMonthJoins,
            projectedMonthIncome,
            growthLabel: successRate >= 65
              ? 'Fast lane'
              : yearStats.applicants >= 15
                ? 'Worth pushing'
                : 'Build demand'
          };
        }).sort((first, second) => second.projectedMonthIncome - first.projectedMonthIncome || second.applicants - first.applicants);

        const forecastMonths = buildYearMonths(currentYear);
        const currentMonthIndex = new Date().getMonth();
        const monthsElapsed = Math.max(currentMonthIndex + 1, 1);
        const pushCourseForecastSeries = pushCourses.map((course, index) => {
          const courseRows = currentYearIntakeRows.filter((row) => row.courseKey === course.courseKey);
          const courseMonthApplications = new Map();
          courseRows.forEach((row) => {
            courseMonthApplications.set(row.monthKey, (courseMonthApplications.get(row.monthKey) || 0) + 1);
          });

          const averageMonthlyApplications = courseRows.length > 0
            ? courseRows.length / monthsElapsed
            : 0;
          const forecastData = forecastMonths.map((month, monthIndex) => {
            const actualApplications = courseMonthApplications.get(month.monthKey) || 0;
            const projectedApplications = monthIndex <= currentMonthIndex
              ? actualApplications
              : averageMonthlyApplications;
            return Math.round(projectedApplications * currentYearSuccessRateDecimal * (Number(course.monthlyFee) || 0));
          });

          return {
            courseKey: course.courseKey,
            courseName: course.courseName,
            data: forecastData,
            currentMonthIncome: forecastData[currentMonthIndex] || 0,
            yearIncome: forecastData.reduce((sum, value) => sum + value, 0),
            colorIndex: index
          };
        });
        const forecastCurrentMonthIncome = pushCourseForecastSeries.reduce((sum, course) => sum + (Number(course.currentMonthIncome) || 0), 0);
        const forecastYearIncome = pushCourseForecastSeries.reduce((sum, course) => sum + (Number(course.yearIncome) || 0), 0);

        const pushCoursesWeight = pushCourses.reduce((sum, row) => sum + (Number(row.applicants) || 0), 0) || pushCourses.length || 1;
        const portfolioMonthlyFee = pushCourses.reduce(
          (sum, row) => sum + ((Number(row.monthlyFee) || 0) * Math.max(Number(row.applicants) || 1, 1)),
          0
        ) / pushCoursesWeight;
        const portfolioStudentsToOneMillion = portfolioMonthlyFee > 0 ? Math.ceil(1000000 / portfolioMonthlyFee) : 0;
        const portfolioProjectedIncome = pushCourses.reduce((sum, row) => sum + (Number(row.projectedMonthIncome) || 0), 0);

        const topSources = Object.entries(sourceCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name, count]) => ({ name, count }));

        const topSourcesCurrentYear = Object.entries(sourceCountsCurrentYear)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name, count]) => ({ name, count }));

        const topSourcesLatest = Object.entries(sourceCountsLatest)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name, count]) => ({ name, count }));

        const completionByCourse = Object.entries(courseStats)
          .map(([name, stat]) => ({
            name,
            total: stat.total,
            completed: stat.completed,
            rate: stat.total ? Math.round((stat.completed / stat.total) * 100) : 0
          }))
          .sort((a, b) => b.rate - a.rate || b.total - a.total)
          .slice(0, 3);

        const dropReasons = Object.entries(dropReasonCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([reason, count]) => ({ reason, count }));

        const cohortProgress = Object.entries(cohortStats)
          .map(([name, stat]) => ({
            name,
            total: stat.total,
            completed: stat.completed,
            active: stat.active
          }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 3);

        const [classesSnap, instructorSnap, loansSnap] = await Promise.all([
          getDocs(collection(db, 'allclasses')),
          getDocs(collection(db, 'Employees')),
          getDocs(collection(db, 'Loans'))
        ]);
        const instructorCounts = {};
        classesSnap.forEach((doc) => {
          const data = doc.data();
          const instructor = data.instructor || 'Unknown';
          instructorCounts[instructor] = (instructorCounts[instructor] || 0) + 1;
        });

        const instructorMap = {};
        instructorSnap.forEach((doc) => {
          const data = doc.data();
          instructorMap[doc.id] = data.employeeName || data.name || 'Instructor';
        });

        const instructorLoad = Object.entries(instructorCounts)
          .map(([id, count]) => ({
            name: instructorMap[id] || id,
            count
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 3);

        const dueSoonDate = new Date();
        dueSoonDate.setDate(dueSoonDate.getDate() + 30);
        let totalPrincipal = 0;
        let dueSoon = 0;
        let totalCount = 0;
        loansSnap.forEach((doc) => {
          const data = doc.data();
          const principal = parseFloat(data.principalAmount || 0) || 0;
          const remaining = parseFloat(data.remainingAmount ?? principal) || 0;
          const dueDate = toDashboardDate(data.dueDate);
          if (remaining > 0) {
            totalPrincipal += remaining;
            totalCount += 1;
          }
          if (remaining > 0 && dueDate && dueDate <= dueSoonDate) {
            dueSoon += 1;
          }
        });

        setAnalytics({
          intakeSnapshot,
          intakeTrend,
          intake: { last7, prev7, last30, prev30 },
          topSources,
          topSourcesCurrentYear,
          topSourcesLatest,
          courseOpportunity,
          pushCourses,
          priorityCourses,
          growthForecast: {
            currentMonthApplications,
            predictedJoins: predictedJoinsThisMonth,
            projectedIncome: predictedIncomeThisMonth,
            successRate: intakeSnapshot.successRate,
            portfolioMonthlyFee,
            portfolioStudentsToOneMillion,
            portfolioProjectedIncome,
            forecastMonths: forecastMonths.map((month) => month.monthLabel),
            forecastSeries: pushCourseForecastSeries,
            forecastCurrentMonthIncome,
            forecastYearIncome
          },
          completionByCourse,
          dropReasons,
          cohortProgress,
          instructorLoad,
          debtRisk: {
            totalPrincipal,
            dueSoon,
            totalCount
          }
        });
      } catch (error) {
        console.error('Failed to load dashboard analytics:', error);
      }
    };

    fetchAnalytics();
  }, [adminRole, currentYear]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const q = query(collection(db, 'users'), where('userStatus', '==', 'Student'));
        const countSnapshot = await getCountFromServer(q);
        setActiveStudents(countSnapshot.data().count);
      } catch (error) {
        console.error('Failed to load active students:', error);
        setStatStates((current) => ({ ...current, activeStudents: 'error' }));
      } finally {
        setStatStates((current) => current.activeStudents === 'error'
          ? current
          : { ...current, activeStudents: 'loaded' });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const now = new Date();
      const monthKey = getMonthKey(now);
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const [incomeRecords, admissionRecords, otherIncomeRecords] = await Promise.all([
        fetchDashboardMonthDocs('Income', { dateFields: ['paymentDate', 'timeCreated'] }, startDate, endDate, monthKey),
        fetchDashboardMonthDocs('AdmissionFeePayments', { dateFields: ['paymentDate', 'createdAt'] }, startDate, endDate, monthKey),
        fetchDashboardMonthDocs('otherIncome', { dateFields: ['dateReceived', 'timeCreated'] }, startDate, endDate, monthKey)
      ]);

      let total = 0;

      filterSchoolFeeIncomeRecords(incomeRecords).forEach((data) => {
        if (getMonthKey(data.paymentDate || data.timeCreated) === monthKey) {
          total += normalizeAmount(data.incomeAmount || data.amount);
        }
      });

      admissionRecords.forEach((data) => {
        if (getMonthKey(data.paymentDate || data.createdAt) === monthKey) {
          total += normalizeAmount(data.amount || data.admissionFeeAmount);
        }
      });

      otherIncomeRecords.forEach((data) => {
        if (getMonthKey(data.dateReceived || data.timeCreated) === monthKey) {
          total += normalizeAmount(data.amount);
        }
      });

      setTotalIncome(total);
      setStatStates((current) => ({ ...current, income: 'loaded' }));
    };

    const loadIncome = async () => {
      try {
        await fetchData();
      } catch (error) {
        console.error('Failed to load dashboard income:', error);
        setStatStates((current) => ({ ...current, income: 'error' }));
      }
    };
    loadIncome();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const now = new Date();
      const monthKey = getMonthKey(now);
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);

      const [expenses, marketingExpenses, billPayments, requisitionPayments, salaryPayments, liabilityPayments] = await Promise.all([
        fetchDashboardMonthDocs('Expenses', { dateFields: ['expenseDate', 'timeStamp'] }, startDate, endDate, monthKey),
        fetchDashboardMonthDocs('marketingExpenses', { dateFields: ['expenseDate', 'createdAt', 'updatedAt'] }, startDate, endDate, monthKey),
        fetchDashboardMonthDocs('BillPayments', {
          dateFields: ['paymentDate', 'createdAt'],
          monthKeyFields: ['billingPeriodKey', 'billPeriodKey']
        }, startDate, endDate, monthKey),
        fetchDashboardMonthDocs('RequisitionPayments', {
          dateFields: ['paymentDate', 'createdAt'],
          monthKeyFields: ['paymentPeriodKey', 'requisitionPeriodKey']
        }, startDate, endDate, monthKey),
        fetchDashboardMonthDocs('SalaryPayments', {
          dateFields: ['paymentDate', 'createdAt'],
          monthKeyFields: ['salaryPeriodKey', 'payrollMonth']
        }, startDate, endDate, monthKey),
        fetchDashboardMonthDocs('LoanPayments', { dateFields: ['paymentDate', 'createdAt'] }, startDate, endDate, monthKey)
      ]);

      let total = 0;

      expenses.forEach((data) => {
        if (getMonthKey(data.expenseDate || data.timeStamp) === monthKey) {
          total += normalizeAmount(data.expenseAmount);
        }
      });

      marketingExpenses.forEach((data) => {
        if (getMonthKey(data.expenseDate || data.createdAt || data.updatedAt) === monthKey) {
          total += normalizeAmount(data.expenseAmount || data.amount);
        }
      });

      billPayments.forEach((data) => {
        if (isSuccessfulBillPayment(data) && resolveBillPaymentPeriodKey(data) === monthKey) {
          total += normalizeAmount(data.amount);
        }
      });

      requisitionPayments.forEach((data) => {
        if (resolveRequisitionPaymentPeriodKey(data) === monthKey) {
          total += normalizeAmount(data.amount);
        }
      });

      salaryPayments.forEach((data) => {
        if (isSuccessfulSalaryPayment(data) && resolveSalaryPaymentPeriodKey(data) === monthKey) {
          total += normalizeAmount(data.amount);
        }
      });

      liabilityPayments.forEach((data) => {
        const status = String(data.paymentStatus || data.status || data.lotusTransactionStatus || '').trim().toLowerCase();
        const isConfirmed = ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded', 'approved'].includes(status);
        if (isConfirmed && getMonthKey(data.paymentDate || data.createdAt) === monthKey) {
          total += normalizeAmount(data.amount);
        }
      });

      setTotalExpenses(total);
      setStatStates((current) => ({ ...current, expenses: 'loaded' }));
    };

    const loadExpenses = async () => {
      try {
        await fetchData();
      } catch (error) {
        console.error('Failed to load dashboard expenses:', error);
        setStatStates((current) => ({ ...current, expenses: 'error' }));
      }
    };
    loadExpenses();
  }, []);



  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAdminRole('admin');
        setLoggedInUser('');
        return;
      }

      try {
        const adminRecord = await resolveAdminRecord(user);
        setAdminRole(normalizeAdminType(adminRecord?.adminType));
        setLoggedInUser(adminRecord?.userName || user.displayName || user.email || '');
      } catch (error) {
        console.error('Failed to resolve dashboard admin role:', error);
        setAdminRole('admin');
        setLoggedInUser(user.displayName || user.email || '');
      }
    });

    return () => unsubscribe();
  }, [auth]);

  const monthlyEntries = Object.entries(monthlyData);
  const totalYearIncome = monthlyEntries.reduce((sum, [, value]) => sum + (parseFloat(value) || 0), 0);
  const averageMonthlyIncome = totalYearIncome / 12;
  const bestMonthEntry = monthlyEntries.reduce(
    (best, current) => ((parseFloat(current[1]) || 0) > (parseFloat(best[1]) || 0) ? current : best),
    ['Jan', 0]
  );
  const bestMonth = bestMonthEntry[0];
  const bestMonthValue = parseFloat(bestMonthEntry[1]) || 0;
  const targetCoverage = MONTHLY_TARGET > 0 ? totalIncome / MONTHLY_TARGET : 0;
  const targetGap = Math.max(MONTHLY_TARGET - totalIncome, 0);
  const targetSurplus = Math.max(totalIncome - MONTHLY_TARGET, 0);
  const targetSavingsGoal = totalIncome * TARGET_SAVINGS_RATE;
  const targetOperatingBalance = totalIncome - totalExpenses;
  const targetBalanceAfterSavings = targetOperatingBalance - targetSavingsGoal;
  const targetStatusLabel = targetCoverage >= 1 ? 'Target ahead' : targetCoverage >= 0.85 ? 'Close to target' : 'Below target';
  const targetChartData = {
    labels: ['Collected', 'Remaining'],
    datasets: [
      {
        data: [Math.min(totalIncome, MONTHLY_TARGET), Math.max(MONTHLY_TARGET - totalIncome, 0)],
        backgroundColor: ['#111827', '#cbd5e1'],
        borderWidth: 0
      }
    ]
  };
  const shouldBlurFinancialData = !showFinancialData;
  const priorityChartData = {
    labels: analytics.priorityCourses.length
      ? analytics.priorityCourses.map((course) => course.courseName)
      : ['No data'],
    datasets: [
      {
        label: 'Priority score',
        data: analytics.priorityCourses.length
          ? analytics.priorityCourses.map((course) => course.priorityScore)
          : [0],
        backgroundColor: DASH_PRIORITY_COLOR,
        borderRadius: 10,
        borderSkipped: false
      }
    ]
  };
  const forecastChartData = {
    labels: analytics.growthForecast.forecastMonths.length
      ? analytics.growthForecast.forecastMonths
      : ['No data'],
    datasets: analytics.growthForecast.forecastSeries.length
      ? analytics.growthForecast.forecastSeries.map((course, index) => {
        const color = DASH_FORECAST_COLORS[index % DASH_FORECAST_COLORS.length];
        return {
          label: course.courseName,
          data: course.data,
          backgroundColor: `${color}CC`,
          borderColor: color,
          borderWidth: 1,
          borderRadius: 8,
          stack: 'forecast'
        };
      })
      : [
        {
          label: 'No data',
          data: [0],
          backgroundColor: '#cbd5e1',
          borderWidth: 0,
          stack: 'forecast'
        }
      ]
  };

  return (
    <div className='dashComp'>
      {/** Chart helpers */}
      {(() => {
        return null;
      })()}
      <div className='dashFlex1'>
        <div className='dash1'>
          <div className='dash1Cont'>
            <h4>Welcome</h4>
            <p>{loggedInUser}</p>
          </div>
          <button
            type="button"
            className="dashFigureToggle"
            onClick={() => setShowFinancialData((current) => !current)}
            aria-label={showFinancialData ? 'Hide dashboard figures' : 'View dashboard figures'}
            aria-pressed={showFinancialData}
            title={showFinancialData ? 'Hide figures' : 'View figures'}
          >
            <FontAwesomeIcon icon={showFinancialData ? faEyeSlash : faEye} />
          </button>
          <img src={dashOne} alt="" />
        </div>

        <div className='dash2'>
          <div className={`dChild1 dashStatCard bg-warning ${shouldBlurFinancialData ? 'dashStatCard--masked' : ''}`}>
            <div className='dashStatRow'>
              <span className='dashStatIcon'>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </span>
              <div className='dashStatMeta'>
                <h5>
                  <DashboardStatValue
                    loading={statStates.activeStudents === 'loading'}
                    error={statStates.activeStudents === 'error'}
                    label="active students"
                    skeletonWidth="3.5rem"
                  >
                    {activeStudents.toLocaleString()}
                  </DashboardStatValue>
                </h5>
                <p>Active Students</p>
              </div>
            </div>
          </div>

          <div className={`dChild2 dashStatCard bg-primary ${shouldBlurFinancialData ? 'dashStatCard--masked' : ''}`}>
            <div className='dashStatRow'>
              <span className='dashStatIcon'>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </span>
              <div className='dashStatMeta'>
                <h5>
                  <DashboardStatValue
                    loading={statStates.income === 'loading'}
                    error={statStates.income === 'error'}
                    label="income this month"
                    skeletonWidth="8rem"
                  >
                    KES {parseFloat(totalIncome).toLocaleString()}
                  </DashboardStatValue>
                </h5>
                <p>Income This Month</p>
              </div>
            </div>
          </div>

          <div className={`dChild3 dashStatCard bg-success ${shouldBlurFinancialData ? 'dashStatCard--masked' : ''}`}>
            <div className='dashStatRow'>
              <span className='dashStatIcon'>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </span>
              <div className='dashStatMeta'>
                <h5>
                  <DashboardStatValue
                    loading={statStates.totalApplicants === 'loading'}
                    error={statStates.totalApplicants === 'error'}
                    label="total applications"
                    skeletonWidth="3.5rem"
                  >
                    {totalApplicants.toLocaleString()}
                  </DashboardStatValue>
                </h5>
                <p>Total Applications</p>
              </div>
            </div>
          </div>

          <div className={`dChild4 dashStatCard bg-danger ${shouldBlurFinancialData ? 'dashStatCard--masked' : ''}`}>
            <div className='dashStatRow'>
              <span className='dashStatIcon'>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </span>
              <div className='dashStatMeta'>
                <h5>
                  <DashboardStatValue
                    loading={statStates.expenses === 'loading'}
                    error={statStates.expenses === 'error'}
                    label="expenses this month"
                    skeletonWidth="8rem"
                  >
                    KES {parseFloat(totalExpenses).toLocaleString()}
                  </DashboardStatValue>
                </h5>
                <p>Expenses this month</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <br />
      <br />

      <div className={`feeIncomeCard ${shouldBlurFinancialData ? 'feeIncomeCard--masked' : ''}`}>
        <div className="feeIncomeHeader">
          <div>
            <h5>Fee Payment Income</h5>
            <p>Monthly trend for {selectedYear} across school fee payments.</p>
          </div>
          <div className="feeIncomeYears">
            {years.map((year) => (
              <button
                key={year}
                className={`feeYearBtn ${selectedYear === year ? 'active' : ''}`}
                onClick={() => handleClick(year)}
                aria-pressed={selectedYear === year}
                type="button"
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        <div className="feeIncomeStats">
          <div className="feeIncomeStat">
            <span>Total ({selectedYear})</span>
            <strong>KES {totalYearIncome.toLocaleString()}</strong>
          </div>
          <div className="feeIncomeStat">
            <span>Average / Month</span>
            <strong>KES {averageMonthlyIncome.toLocaleString()}</strong>
          </div>
          <div className="feeIncomeStat">
            <span>Best Month</span>
            <strong>{bestMonth} · KES {bestMonthValue.toLocaleString()}</strong>
          </div>
        </div>

        <div className="feeIncomeChart">
          <Line
            className="theFeeLineGraph"
            datasetIdKey="id"
            data={{
              labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
              datasets: [
                {
                  id: 1,
                  label: `Fee payment income (${selectedYear})`,
                  data: [
                    monthlyData.Jan, monthlyData.Feb, monthlyData.Mar, monthlyData.Apr,
                    monthlyData.May, monthlyData.Jun, monthlyData.Jul, monthlyData.Aug,
                    monthlyData.Sep, monthlyData.Oct, monthlyData.Nov, monthlyData.Dec
                  ],
                  backgroundColor: (context) => {
                    const { chart } = context;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return 'rgba(14, 165, 233, 0.15)';
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(14, 165, 233, 0.35)');
                    gradient.addColorStop(1, 'rgba(14, 165, 233, 0.02)');
                    return gradient;
                  },
                  borderColor: '#0ea5e9',
                  pointBackgroundColor: '#0ea5e9',
                  pointBorderColor: '#ffffff',
                  pointBorderWidth: 2,
                  pointRadius: 3,
                  pointHoverRadius: 5,
                  borderWidth: 2.5,
                  fill: true,
                  tension: 0.35
                }
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      let label = context.dataset.label || '';
                      if (label) {
                        label += ': ';
                      }
                      if (context.parsed.y !== null) {
                        label += context.parsed.y.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'KES',
                        });
                      }
                      return label;
                    },
                  },
                },
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: { color: '#94a3b8' }
                },
                y: {
                  grid: { color: 'rgba(15, 27, 45, 0.08)' },
                  ticks: {
                    color: '#94a3b8',
                    callback: (value) => `KES ${Number(value).toLocaleString()}`
                  }
                }
              }
            }}
          />
        </div>
      </div>

      {adminRole === 'superAdmin' && (
      <div className="dashAnalytics">
        <h5>Analytics Snapshot</h5>
        <div className="dashAnalyticsGrid">
          <div className="dashAnalyticsCard dashAnalyticsCard--intake">
            <div className="dashAnalyticsCardHeader">
              <div>
                <h6>Intake Analysis Snapshot</h6>
                <p className="dashAnalyticsLead">Joined vs pending applicants across the current year so far.</p>
              </div>
              <div className="dashAnalyticsTrendPill">
                <span>{analytics.intakeSnapshot.latestMonthLabel}</span>
                <strong>{analytics.intakeSnapshot.latestMonthSuccessRate.toFixed(0)}%</strong>
                <small>
                  {formatSignedPercent(analytics.intakeSnapshot.monthOverMonth, 1)} vs prev month
                </small>
              </div>
            </div>

            <div className="dashAnalyticsSummaryGrid">
              <div>
                <span>Success rate</span>
                <strong>{analytics.intakeSnapshot.successRate.toFixed(1)}%</strong>
                <p>Joined vs did not join for this year</p>
              </div>
              <div>
                <span>Applications this year</span>
                <strong>{analytics.intakeSnapshot.applicants.toLocaleString()}</strong>
                <p>Current-year intake records only</p>
              </div>
              <div>
                <span>Joined</span>
                <strong>{analytics.intakeSnapshot.joined.toLocaleString()}</strong>
                <p>Students who converted</p>
              </div>
              <div>
                <span>Pending</span>
                <strong>{analytics.intakeSnapshot.pending.toLocaleString()}</strong>
                <p>Still waiting to close this year</p>
              </div>
            </div>

            <div className="dashChartWrap dashChartTight">
              <Bar
                data={{
                  labels: analytics.intakeTrend.length ? analytics.intakeTrend.map((item) => item.monthLabel) : ['No data'],
                  datasets: [
                    {
                      label: 'Applicants',
                      data: analytics.intakeTrend.length ? analytics.intakeTrend.map((item) => item.applicants) : [0],
                      backgroundColor: 'rgba(148, 163, 184, 0.6)',
                      borderRadius: 8,
                      barThickness: 14
                    },
                    {
                      label: 'Joined',
                      data: analytics.intakeTrend.length ? analytics.intakeTrend.map((item) => item.joined) : [0],
                      backgroundColor: 'rgba(15, 118, 110, 0.85)',
                      borderRadius: 8,
                      barThickness: 14
                    },
                    {
                      label: 'Pending',
                      data: analytics.intakeTrend.length ? analytics.intakeTrend.map((item) => item.pending) : [0],
                      backgroundColor: 'rgba(203, 213, 225, 0.95)',
                      borderRadius: 8,
                      barThickness: 14
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        color: '#64748b',
                        boxWidth: 12,
                        boxHeight: 12
                      }
                    }
                  },
                  scales: {
                    x: {
                      grid: { display: false },
                      ticks: { color: '#94a3b8' }
                    },
                    y: {
                      beginAtZero: true,
                      grid: { color: 'rgba(15, 27, 45, 0.06)' },
                      ticks: { color: '#94a3b8', precision: 0 }
                    }
                  }
                }}
              />
            </div>

            <div className="dashAnalyticsList">
              <div><span>Did not join</span><strong>{analytics.intakeSnapshot.didNotJoin.toLocaleString()}</strong></div>
              <div><span>Left after joining</span><strong>{analytics.intakeSnapshot.leftAfterJoining.toLocaleString()}</strong></div>
            </div>
          </div>

          <div className={`dashAnalyticsCard dashAnalyticsFinancialCard ${shouldBlurFinancialData ? 'dashAnalyticsFinancialCard--masked' : ''}`}>
            <div className="dashAnalyticsCardHeader">
              <div>
                <h6>Monthly Target Snapshot</h6>
                <p className="dashAnalyticsLead">A quick look at the month&apos;s target, what has been collected, and how much room is left.</p>
              </div>
              <div className="dashAnalyticsTrendPill">
                <span>{targetStatusLabel}</span>
                <strong>{Math.round(Math.min(targetCoverage * 100, 100))}%</strong>
                <small>
                  {targetCoverage >= 1 ? 'Target achieved' : <><span className="dashPrivateFigure">{targetGap.toLocaleString()}</span> still needed</>}
                </small>
              </div>
            </div>

            <div className="dashChartWrap dashChartTight">
              <Doughnut
                data={targetChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '72%',
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        color: '#64748b',
                        boxWidth: 12,
                        boxHeight: 12
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const label = context.label || '';
                          const value = Number(context.raw || 0).toLocaleString();
                          return `${label}: KES ${value}`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>

            <div className="dashAnalyticsSummaryGrid">
              <div>
                <span>Collected</span>
                <strong>KES {totalIncome.toLocaleString()}</strong>
                <p>This month&apos;s receipts</p>
              </div>
              <div>
                <span>Target</span>
                <strong>KES {MONTHLY_TARGET.toLocaleString()}</strong>
                <p>Monthly goal overview</p>
              </div>
              <div>
                <span>{targetGap > 0 ? 'Gap' : 'Surplus'}</span>
                <strong>KES {(targetGap > 0 ? targetGap : targetSurplus).toLocaleString()}</strong>
                <p>{targetGap > 0 ? 'Still needed to hit target' : 'Collected above target'}</p>
              </div>
              <div>
                <span>After expenses + savings</span>
                <strong>KES {targetBalanceAfterSavings.toLocaleString()}</strong>
                <p>What remains after the 20% savings rule</p>
              </div>
            </div>

            <div className="dashAnalyticsList">
              <div><span>Savings rule</span><strong>{Math.round(TARGET_SAVINGS_RATE * 100)}%</strong></div>
              <div><span>Recorded expenses</span><strong>KES {totalExpenses.toLocaleString()}</strong></div>
              <div><span>Operating balance</span><strong>KES {targetOperatingBalance.toLocaleString()}</strong></div>
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Top Performing Sources</h6>
            <div className="dashChartWrap">
              <Bar
                data={{
                  labels: analytics.topSources.length ? analytics.topSources.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Leads',
                    data: analytics.topSources.length ? analytics.topSources.map((item) => item.count) : [0],
                    backgroundColor: '#6366f1',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.topSources.length === 0 && <p className="dashAnalyticsEmpty">No source data yet.</p>}
              {analytics.topSources.map((item) => (
                <div key={item.name}><span>{item.name}</span><strong>{item.count}</strong></div>
              ))}
            </div>
            <div className="dashAnalyticsDivider"></div>
            <h6 className="dashAnalyticsSubTitle">Top Sources This Year ({currentYear})</h6>
            <div className="dashChartWrap dashChartSmall">
              <Bar
                data={{
                  labels: analytics.topSourcesCurrentYear.length ? analytics.topSourcesCurrentYear.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Leads',
                    data: analytics.topSourcesCurrentYear.length ? analytics.topSourcesCurrentYear.map((item) => item.count) : [0],
                    backgroundColor: '#14b8a6',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.topSourcesCurrentYear.length === 0 && <p className="dashAnalyticsEmpty">No current-year source data yet.</p>}
              {analytics.topSourcesCurrentYear.map((item) => (
                <div key={item.name}><span>{item.name}</span><strong>{item.count}</strong></div>
              ))}
            </div>
            <div className="dashAnalyticsDivider"></div>
            <h6 className="dashAnalyticsSubTitle">Latest Top Sources (30 days)</h6>
            <div className="dashChartWrap dashChartSmall">
              <Bar
                data={{
                  labels: analytics.topSourcesLatest.length ? analytics.topSourcesLatest.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Leads',
                    data: analytics.topSourcesLatest.length ? analytics.topSourcesLatest.map((item) => item.count) : [0],
                    backgroundColor: '#22c55e',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.topSourcesLatest.length === 0 && <p className="dashAnalyticsEmpty">No recent source data yet.</p>}
              {analytics.topSourcesLatest.map((item) => (
                <div key={item.name}><span>{item.name}</span><strong>{item.count}</strong></div>
              ))}
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Completion Rate by Course</h6>
            <div className="dashChartWrap dashChartSmall">
              <Bar
                data={{
                  labels: analytics.completionByCourse.length ? analytics.completionByCourse.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Completion %',
                    data: analytics.completionByCourse.length ? analytics.completionByCourse.map((item) => item.rate) : [0],
                    backgroundColor: '#14b8a6',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, max: 100, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.completionByCourse.length === 0 && <p className="dashAnalyticsEmpty">No completion data yet.</p>}
              {analytics.completionByCourse.map((item) => (
                <div key={item.name}><span>{item.name}</span><strong>{item.rate}%</strong></div>
              ))}
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Drop-off Reasons</h6>
            <div className="dashChartWrap dashChartSmall">
              <Bar
                data={{
                  labels: analytics.dropReasons.length ? analytics.dropReasons.map((item) => item.reason) : ['No data'],
                  datasets: [{
                    label: 'Count',
                    data: analytics.dropReasons.length ? analytics.dropReasons.map((item) => item.count) : [0],
                    backgroundColor: '#ef4444',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  indexAxis: 'y',
                  scales: {
                    x: { beginAtZero: true, grid: { color: 'rgba(15, 27, 45, 0.06)' } },
                    y: { grid: { display: false } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.dropReasons.length === 0 && <p className="dashAnalyticsEmpty">No drop-off reasons recorded.</p>}
              {analytics.dropReasons.map((item) => (
                <div key={item.reason}><span>{item.reason}</span><strong>{item.count}</strong></div>
              ))}
            </div>
          </div>

          <div className={`dashAnalyticsCard dashAnalyticsFinancialCard ${shouldBlurFinancialData ? 'dashAnalyticsFinancialCard--masked' : ''}`}>
            <h6>Debt Risk</h6>
            <div className="dashChartWrap">
              <Doughnut
                data={{
                  labels: ['Due in 30 days', 'Other'],
                  datasets: [{
                    data: [
                      analytics.debtRisk.dueSoon,
                      Math.max(analytics.debtRisk.totalCount - analytics.debtRisk.dueSoon, 0)
                    ],
                    backgroundColor: ['#f59e0b', '#94a3b8'],
                    borderWidth: 0
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: 'bottom' } }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              <div><span>Active loans</span><strong>{analytics.debtRisk.totalCount}</strong></div>
              <div><span>Due in 30 days</span><strong>{analytics.debtRisk.dueSoon}</strong></div>
              <div><span>Total principal</span><strong>KES {parseFloat(analytics.debtRisk.totalPrincipal).toLocaleString()}</strong></div>
            </div>
          </div>

          <div className={`dashAnalyticsCard dashAnalyticsCard--growth dashAnalyticsFinancialCard ${shouldBlurFinancialData ? 'dashAnalyticsFinancialCard--masked' : ''}`}>
            <div className="dashAnalyticsCardHeader">
              <div>
                <h6>Course Growth Radar</h6>
                <p className="dashAnalyticsLead">Courses to concentrate on, the four priority push courses, and a success forecast for this year.</p>
              </div>
              <div className="dashAnalyticsTrendPill">
                <span>Current year</span>
                <strong>{analytics.growthForecast.successRate.toFixed(0)}%</strong>
                <small><span className="dashPrivateFigure">{analytics.growthForecast.predictedJoins.toLocaleString()}</span> projected joins this month</small>
              </div>
            </div>

            <div className="dashAnalyticsSummaryGrid">
              <div>
                <span>Current-year success</span>
                <strong>{analytics.growthForecast.successRate.toFixed(1)}%</strong>
                <p>Joined vs did not join</p>
              </div>
              <div>
                <span>Applications this month</span>
                <strong>{analytics.growthForecast.currentMonthApplications.toLocaleString()}</strong>
                <p>All current-month applications</p>
              </div>
              <div>
                <span>Forecast joins</span>
                <strong>{analytics.growthForecast.predictedJoins.toLocaleString()}</strong>
                <p>Based on this year&apos;s success rate</p>
              </div>
              <div>
                <span>Forecast income</span>
                <strong>KES {analytics.growthForecast.projectedIncome.toLocaleString()}</strong>
                <p>Projected from this month&apos;s application mix</p>
              </div>
            </div>

            <div className="dashGrowthCharts">
            <div className="dashGrowthChartPanel">
              <div className="dashGrowthChartPanelHeader">
                <div>
                  <strong>Priority score ranking</strong>
                    <span>Higher score means the course deserves more push right now.</span>
                  </div>
                  <div className="dashGrowthBadge">Ranked chart</div>
                </div>
                <div className="dashChartWrap dashGrowthChartWrap">
                  <Bar
                    data={priorityChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: 'y',
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const source = analytics.priorityCourses[context.dataIndex];
                              return [
                                `Priority score: ${Number(context.parsed.x || 0).toLocaleString()}`,
                                `Applicants: ${source?.applicants?.toLocaleString?.() || 0}`,
                                `Success rate: ${Number(source?.successRate || 0).toFixed(0)}%`
                              ];
                            }
                          }
                        }
                      },
                      scales: {
                        x: {
                          beginAtZero: true,
                          grid: { color: 'rgba(15, 27, 45, 0.06)' },
                          ticks: {
                            callback: (value) => Number(value).toLocaleString()
                          }
                        },
                        y: { grid: { display: false } }
                      }
                    }}
                  />
                </div>
              </div>

              <div className="dashGrowthChartPanel">
                <div className="dashGrowthChartPanelHeader">
                  <div>
                    <strong>Push-course income forecast</strong>
                    <span>Month-by-month outlook for Data Science, Machine Learning, UI/UX, and Full Stack.</span>
                  </div>
                  <div className="dashGrowthBadge dashGrowthBadge--teal"><span className="dashPrivateFigure">KES {analytics.growthForecast.forecastYearIncome.toLocaleString()}</span> year</div>
                </div>
                <div className="dashChartWrap dashGrowthChartWrap">
                  <Bar
                    data={forecastChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'bottom' },
                        tooltip: {
                          callbacks: {
                            label: (context) => `${context.dataset.label}: KES ${Number(context.parsed.y || 0).toLocaleString()}`
                          }
                        }
                      },
                      scales: {
                        x: {
                          stacked: true,
                          grid: { color: 'rgba(15, 27, 45, 0.06)' }
                        },
                        y: {
                          stacked: true,
                          beginAtZero: true,
                          grid: { color: 'rgba(15, 27, 45, 0.06)' },
                          ticks: {
                            callback: (value) => `KES ${Number(value).toLocaleString()}`
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="dashAnalyticsList dashGrowthForecastSummary">
                  <div>
                    <span>This month forecast</span>
                    <strong>KES {analytics.growthForecast.forecastCurrentMonthIncome.toLocaleString()}</strong>
                  </div>
                  <div>
                    <span>Year forecast</span>
                    <strong>KES {analytics.growthForecast.forecastYearIncome.toLocaleString()}</strong>
                  </div>
                </div>
              </div>
            </div>

            <h6 className="dashAnalyticsSubTitle">Courses to concentrate on</h6>
            <div className="dashGrowthList">
              {analytics.courseOpportunity.length === 0 && <p className="dashAnalyticsEmpty">No course opportunity data yet.</p>}
              {analytics.courseOpportunity.map((course, index) => (
                <div key={course.courseKey} className="dashGrowthCard">
                  <div className="dashGrowthCardHeader">
                    <div>
                      <strong>{index + 1}. {course.courseName}</strong>
                      <span>{course.growthLabel} • {course.applicants.toLocaleString()} applications this year</span>
                    </div>
                    <div className="dashGrowthBadge"><span className="dashPrivateFigure">{course.successRate.toFixed(0)}%</span> success</div>
                  </div>
                  <div className="dashGrowthMeta">
                  <span>Joined {course.joined.toLocaleString()}</span>
                    <span>Monthly fee <strong className="dashPrivateFigure">KES {course.monthlyFee.toLocaleString()}</strong></span>
                    <span><strong className="dashPrivateFigure">KES {course.currentYearIncome.toLocaleString()}</strong> monthly income</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="dashAnalyticsDivider"></div>

            <h6 className="dashAnalyticsSubTitle">Courses to push</h6>
            <div className="dashGrowthList">
              {analytics.pushCourses.length === 0 && <p className="dashAnalyticsEmpty">No push course data yet.</p>}
              {analytics.pushCourses.map((course) => (
                <div key={course.courseKey} className="dashGrowthCard dashGrowthCard--push">
                  <div className="dashGrowthCardHeader">
                    <div>
                      <strong>{course.courseName}</strong>
                      <span>{course.growthLabel} • {course.currentMonthApplications.toLocaleString()} applications this month</span>
                    </div>
                    <div className="dashGrowthBadge dashGrowthBadge--teal"><span className="dashPrivateFigure">{course.studentsToOneMillion.toLocaleString()}</span> to 1M</div>
                  </div>
                  <div className="dashGrowthMeta">
                    <span>Monthly fee <strong className="dashPrivateFigure">KES {course.monthlyFee.toLocaleString()}</strong></span>
                    <span>Projected joins {course.predictedMonthJoins.toLocaleString()}</span>
                    <span>Projected income <strong className="dashPrivateFigure">KES {course.projectedMonthIncome.toLocaleString()}</strong></span>
                  </div>
                </div>
              ))}
            </div>

            <div className="dashAnalyticsDivider"></div>

            <div className="dashAnalyticsSummaryGrid">
              <div>
                <span>Portfolio fee</span>
                <strong>KES {Math.round(analytics.growthForecast.portfolioMonthlyFee).toLocaleString()}</strong>
                <p>Weighted fee across push courses</p>
              </div>
              <div>
                <span>Students to 1M</span>
                <strong>{analytics.growthForecast.portfolioStudentsToOneMillion.toLocaleString()}</strong>
                <p>Blended mix across the push list</p>
              </div>
              <div>
                <span>Forecast income</span>
                <strong>KES {analytics.growthForecast.portfolioProjectedIncome.toLocaleString()}</strong>
                <p>From this month&apos;s push-course mix</p>
              </div>
              <div>
                <span>Net monthly target</span>
                <strong>KES {Math.max(1000000 - analytics.growthForecast.projectedIncome, 0).toLocaleString()}</strong>
                <p>Gap after forecasted intake income</p>
              </div>
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Cohort Progress</h6>
            <div className="dashChartWrap">
              <Bar
                data={{
                  labels: analytics.cohortProgress.length ? analytics.cohortProgress.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Completion %',
                    data: analytics.cohortProgress.length
                      ? analytics.cohortProgress.map((item) => (item.total ? Math.round((item.completed / item.total) * 100) : 0))
                      : [0],
                    backgroundColor: '#6366f1',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, max: 100, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.cohortProgress.length === 0 && <p className="dashAnalyticsEmpty">No cohort data yet.</p>}
              {analytics.cohortProgress.map((item) => (
                <div key={item.name}>
                  <span>{item.name}</span>
                  <strong>{item.completed}/{item.total} completed</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Instructor Load</h6>
            <div className="dashChartWrap">
              <Bar
                data={{
                  labels: analytics.instructorLoad.length ? analytics.instructorLoad.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Classes',
                    data: analytics.instructorLoad.length ? analytics.instructorLoad.map((item) => item.count) : [0],
                    backgroundColor: '#0ea5e9',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.instructorLoad.length === 0 && <p className="dashAnalyticsEmpty">No instructor load yet.</p>}
              {analytics.instructorLoad.map((item) => (
                <div key={item.name}><span>{item.name}</span><strong>{item.count} classes</strong></div>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default DashComp;
